import React, { useState } from "react";
import { cn } from "@/lib/utils"; // adjust based on your setup

type InputProps = React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> & {
  icon?: React.ReactNode;
  maxLength?: number;
};

function Input({
  className,
  type = "text",
  icon,
  maxLength,
  onChange,
  ref,
  ...props
}: InputProps & { ref?: React.Ref<HTMLInputElement> }) {
  const [charCount, setCharCount] = useState(props.value?.toString().length ?? 0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCharCount(e.target.value.length);
    onChange?.(e);
  };

  const isTextarea = type === "textarea";

  return (
    <div
      className={cn(
        "group relative flex gap-3 border border-input-border bg-input-surface rounded-(--border-radius-150) px-3 py-2 transition-[color,box-shadow] hover:border-input-border-hover hover:shadow-sm focus-within:outline-lavender-3 focus-within:outline-offset-1 focus-within:outline-2 focus-within:shadow-sm",
        props.disabled && "cursor-not-allowed bg-bg-surface-disabled pointer-events-none",
        "active:border-input-border-hover active:shadow-sm",
        isTextarea ? "h-32 flex-col items-end justify-start" : "items-center",
        "body-lg",
        className
      )}
    >
      {icon && (
        <div className="flex items-center pointer-events-none size-4 text-secondary">{icon}</div>
      )}

      {isTextarea ? (
        <textarea
          maxLength={maxLength}
          onChange={handleChange}
          className="flex-1 w-full resize-none bg-transparent focus:outline-none text-left"
          {...props}
        />
      ) : (
        <input
          ref={ref}
          type={type}
          maxLength={maxLength}
          onChange={handleChange}
          className="flex-1 bg-transparent focus:outline-none text-left"
          {...props}
        />
      )}

      {maxLength && (
        <div className="body-sm text-secondary self-end">
          {charCount}/{maxLength}
        </div>
      )}
    </div>
  );
}

export { Input };
